/**
 * reveal — スクロールで要素をフェードイン（progressive enhancement）
 *
 * - **既定は表示**（CSS の `.c-reveal` は素のまま）。JS が動いて初めて
 *   `<html data-sp-reveal-armed>` を付け、そこから CSS が初期非表示 → reveal を担う。
 *   → JS が読み込まれない / 例外で落ちても本文は必ず読める。
 * - JS 側は状態クラス（is-visible）の付与だけ。見た目は CSS。
 * - prefers-reduced-motion / IntersectionObserver 非対応時は arm せず（＝表示のまま）。
 * - arm する前に、すでに画面内にある要素は同期的に is-visible にして「隠して即戻す」チラつきを防ぐ。
 */

const SELECTOR = "[data-sp-reveal]";
const VISIBLE_CLASS = "is-visible";
const ARMED_ATTR = "data-sp-reveal-armed";

export function initReveal(root = document) {
  const elements = Array.from(root.querySelectorAll(SELECTOR));
  if (elements.length === 0) {
    return;
  }

  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (reduceMotion || !("IntersectionObserver" in window)) {
    // arm しない = 既定の表示のまま。何もしない。
    return;
  }

  // すでにビューポート内（やや下まで）にある要素は先に visible にしておく。
  const viewportH = window.innerHeight || document.documentElement.clientHeight;
  elements.forEach((el) => {
    const rect = el.getBoundingClientRect();
    if (rect.top < viewportH * 0.92 && rect.bottom > 0) {
      el.classList.add(VISIBLE_CLASS);
    }
  });

  // ここで初めて「初期非表示 → reveal」の CSS を有効化する。
  document.documentElement.setAttribute(ARMED_ATTR, "");

  const observer = new IntersectionObserver(
    (entries, obs) => {
      entries.forEach((entry) => {
        // 画面より背の高い要素（出典の長い一覧など）は、12% が同時に画面へ入ることがなく、いつまでも表示されない。
        // そうした要素は一部でも入った時点で表示する（2026-10-22: PHP の脆弱性の記事の出典 119 件が表示されなかった）。
        const rootH = entry.rootBounds ? entry.rootBounds.height : window.innerHeight;
        const tall = entry.boundingClientRect.height > rootH;
        if (entry.isIntersecting && (tall || entry.intersectionRatio >= 0.12)) {
          entry.target.classList.add(VISIBLE_CLASS);
          obs.unobserve(entry.target);
        }
      });
    },
    { threshold: [0, 0.12], rootMargin: "0px 0px -8% 0px" }
  );

  elements.forEach((el) => {
    if (!el.classList.contains(VISIBLE_CLASS)) {
      observer.observe(el);
    }
  });
}
